import React, { useState, useEffect } from "react";
import {
  Building2,
  Users,
  CreditCard,
  Settings,
  TrendingUp,
  DollarSign,
  Shield,
  AlertCircle,
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  Download,
  BarChart3,
  UserCheck,
  Activity,
  X,
  Ticket,
  Clock,
  MessageSquare,
  CheckCircle,
  AlertTriangle,
  User,
  Tag,
  Paperclip,
  Send,
  LogOut,
  Layers,
} from "lucide-react";
import {
  ProgressivePricingPlan,
  ProgressivePlansResponse,
  ProgressivePricingTire,
  ProgressivePricingType,
} from "../../types/types";
import { realAPI } from "../../services/realAPI";

interface SuperAdminDashboardProps {
  onLogout: () => void;
}

// Progressive Plan Balance interface
interface ProgressivePlanBalance {
  planId: number;
  planKey: string;
  progressivePricingType: "STORIES" | "USERS" | string;
  quota: number;
  balance: number;
  isActive: boolean;
  isDefault: boolean;
}

// API Organization interfaces based on real API response
interface APIOrganization {
  id: number;
  name: string;
  industry: string;
  size: string;
  website: string;
  status: "ACTIVE" | "SUSPENDED" | "INACTIVE" | null;
  createdAt: string | null;
  lastActive: string;
  monthlyRevenue: number | null;
  users: number;
  teams: number;
  adminEmail: string | null;
  progressivePlanBalances: ProgressivePlanBalance[];
  plan: {
    id: number;
    key: string;
    name: string;
    description: string;
    maxUsers: number | null;
    maxTeams: number | null;
    storageBytes: number | null;
    isMostPopular: boolean;
    isActive: boolean | null;
    createdAt: string;
    updatedAt: string;
    apiCalls: number | null;
    supportType: string | null;
    organizationCount: number | null;
    billingOptions: PlanBillingOption[];
    features: PlanFeature[];
    limits: PlanLimits;
  } | null;
}

// Legacy Organization interface for UI compatibility
interface Organization {
  id: string;
  name: string;
  industry: string;
  size: string;
  plan: string;
  status: "active" | "suspended" | "trial";
  createdAt: string;
  lastActive: string;
  users: number;
  teams: number;
  monthlyRevenue: number;
  adminEmail: string;
  website?: string;
  progressivePlanBalances: ProgressivePlanBalance[];
}

// API Plan interfaces based on real API response
interface PlanBillingOption {
  id: number;
  billingCycle: "MONTHLY" | "ANNUAL";
  priceCents: number;
  formattedPrice: string;
  isDefault: boolean;
  savingsPercentage: number | null;
}

interface PlanFeature {
  featureId: number;
  featureName: string;
  featureCode: string;
  value: string;
}

interface PlanLimits {
  maxUsers: number;
  maxTeams: number;
  storageBytes: number | null;
  formattedStorage: string;
  supportType: string;
}

interface Plan {
  id: number;
  key: string;
  name: string;
  description: string;
  isMostPopular: boolean;
  billingOptions: PlanBillingOption[];
  features: PlanFeature[];
  limits: PlanLimits;
  organizationCount: number;
  createdAt: string;
  updatedAt: string;
}

// Plan update interfaces
interface PlanUpdateRequest {
  name: string;
  description: string;
  isMostPopular: boolean;
  billingOptions: {
    id?: number;
    billingCycle: "MONTHLY" | "ANNUAL";
    priceCents: number;
    isDefault: boolean;
    savingsPercentage?: number | null;
  }[];
  features: {
    featureId?: number;
    featureName: string;
    featureCode: string;
    value: string;
  }[];
  limits: {
    maxUsers: number;
    maxTeams: number;
    storageBytes: number | null;
    supportType: string;
  };
}

interface SystemMetrics {
  totalOrganizations: number;
  totalUsers: number;
  totalRevenue: number;
  activeTrials: number;
  conversionRate: number;
  churnRate: number;
  averageTeamsPerOrg: number;
  supportTickets: number;
}

// Support Ticket interfaces
interface SupportTicket {
  id: string;
  ticketNumber: string;
  subject: string;
  description: string;
  priority: "low" | "medium" | "high" | "urgent";
  status: "open" | "in-progress" | "resolved" | "closed";
  category:
    | "technical"
    | "billing"
    | "account"
    | "feature-request"
    | "bug"
    | "general";
  organizationId: string;
  organizationName: string;
  userEmail: string;
  userName: string;
  assignedTo?: string;
  assignedToName?: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  tags: string[];
  attachments: string[];
  comments: TicketComment[];
}

interface TicketComment {
  id: string;
  ticketId: string;
  author: string;
  authorType: "user" | "support" | "system";
  message: string;
  isInternal: boolean;
  createdAt: string;
  attachments: string[];
}

const SuperAdminDashboard: React.FC<SuperAdminDashboardProps> = ({
  onLogout,
}) => {
  // Logout handler
  const handleLogout = () => {
    onLogout();
  };

  const [activeTab, setActiveTab] = useState<
    | "overview"
    | "organizations"
    | "plans"
    | "progressive-plans"
    | "analytics"
    | "tickets"
    | "settings"
  >("overview");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<
    "all" | "active" | "suspended" | "trial"
  >("all");
  const [filterPlan, setFilterPlan] = useState<
    "all" | "free" | "professional" | "enterprise"
  >("all");
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [showCreateOrg, setShowCreateOrg] = useState(false);

  // Real API state
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updateLoading, setUpdateLoading] = useState(false);

  // Progressive Plans state
  const [progressivePlans, setProgressivePlans] = useState<
    ProgressivePricingPlan[]
  >([]);
  const [progressivePlansLoading, setProgressivePlansLoading] = useState(false);
  const [progressivePlansError, setProgressivePlansError] = useState<
    string | null
  >(null);
  const [editingProgressivePlan, setEditingProgressivePlan] =
    useState<ProgressivePricingPlan | null>(null);
  const [showCreateProgressivePlan, setShowCreateProgressivePlan] =
    useState(false);

  // Real organizations data from API
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [organizationsError, setOrganizationsError] = useState<string | null>(
    null
  );
  const [editingOrganization, setEditingOrganization] =
    useState<Organization | null>(null);
  const [organizationUpdateLoading, setOrganizationUpdateLoading] =
    useState(false);
  const [organizationCreateLoading, setOrganizationCreateLoading] =
    useState(false);

  // Progressive Plan Topup state
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [topupOrganization, setTopupOrganization] =
    useState<Organization | null>(null);
  const [topupLoading, setTopupLoading] = useState(false);

  // Function to convert API organization to UI organization
  const convertAPIOrganizationToUIOrganization = (
    apiOrg: APIOrganization
  ): Organization => {
    console.log(`Converting ${apiOrg.name}:`, {
      hasProgressiveBalances: !!apiOrg.progressivePlanBalances,
      progressiveBalancesLength: apiOrg.progressivePlanBalances?.length || 0,
      progressiveBalances: apiOrg.progressivePlanBalances,
    });

    return {
      id: apiOrg.id.toString(),
      name: apiOrg.name,
      industry: apiOrg.industry,
      size: apiOrg.size,
      plan: apiOrg.plan?.name || "Free",
      status:
        apiOrg.status === "ACTIVE"
          ? "active"
          : apiOrg.status === "SUSPENDED"
          ? "suspended"
          : apiOrg.status === "INACTIVE"
          ? "suspended"
          : "trial",
      createdAt: apiOrg.createdAt || apiOrg.lastActive,
      lastActive: apiOrg.lastActive,
      users: apiOrg.users,
      teams: apiOrg.teams,
      monthlyRevenue: apiOrg.monthlyRevenue || 0,
      adminEmail: apiOrg.adminEmail || "",
      website: apiOrg.website || "",
      progressivePlanBalances: apiOrg.progressivePlanBalances || [],
    };
  };

  // Fetch organizations from API
  const fetchOrganizations = async () => {
    try {
      setOrganizationsLoading(true);
      setOrganizationsError(null);

      console.log("Fetching organizations from API...");
      const response = await realAPI.superAdmin.getAllOrganizations(1, 100); // Fetch up to 100 organizations (page 1-based)

      console.log("Organizations API response:", response);

      if (response && response.data && Array.isArray(response.data)) {
        // Debug: Check the first organization's progressive plan balances
        if (response.data.length > 0) {
          console.log("First organization raw data:", response.data[0]);
          console.log(
            "Progressive plan balances in first org:",
            response.data[0].progressivePlanBalances
          );
        }

        const uiOrganizations = response.data.map(
          convertAPIOrganizationToUIOrganization
        );
        setOrganizations(uiOrganizations);
        console.log("Successfully loaded organizations:", uiOrganizations);

        // Debug: Check the converted organizations
        if (uiOrganizations.length > 0) {
          console.log("First converted organization:", uiOrganizations[0]);
          console.log(
            "Progressive plan balances in converted org:",
            uiOrganizations[0].progressivePlanBalances
          );
        }
      } else {
        console.error("Invalid organizations response format:", response);
        setOrganizationsError("Invalid response format from server");
      }
    } catch (error) {
      console.error("Error fetching organizations:", error);
      setOrganizationsError(
        error instanceof Error ? error.message : "Failed to fetch organizations"
      );
    } finally {
      setOrganizationsLoading(false);
    }
  };

  // Update organization function
  const updateOrganization = async (
    organizationId: string,
    updateData: Partial<Organization>
  ) => {
    try {
      setOrganizationUpdateLoading(true);
      setOrganizationsError(null);

      console.log("Updating organization:", organizationId, updateData);

      // Convert UI data to API format
      const apiUpdateData: any = {
        name: updateData.name,
        industry: updateData.industry,
        size: updateData.size,
        website: updateData.website,
        adminEmail: updateData.adminEmail,
      };

      // Convert status to API format
      if (updateData.status) {
        apiUpdateData.status =
          updateData.status === "active"
            ? "ACTIVE"
            : updateData.status === "suspended"
            ? "SUSPENDED"
            : updateData.status === "trial"
            ? "INACTIVE"
            : "ACTIVE";
      }

      // Add monthly revenue if provided
      if (updateData.monthlyRevenue !== undefined) {
        apiUpdateData.monthlyRevenue = updateData.monthlyRevenue;
      }

      const response = await realAPI.superAdmin.updateOrganization(
        organizationId,
        apiUpdateData
      );

      console.log("Organization update response:", response);

      if (response && response.success && response.data) {
        // Update the organization in the local state
        const updatedUIOrganization = convertAPIOrganizationToUIOrganization(
          response.data
        );
        setOrganizations((prevOrgs) =>
          prevOrgs.map((org) =>
            org.id === organizationId ? updatedUIOrganization : org
          )
        );

        setEditingOrganization(null);
        console.log(
          "Organization updated successfully:",
          updatedUIOrganization
        );
        return { success: true };
      } else {
        const errorMessage =
          response?.error?.message || "Failed to update organization";
        setOrganizationsError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error("Error updating organization:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update organization";
      setOrganizationsError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setOrganizationUpdateLoading(false);
    }
  };

  // Create organization function
  const createOrganization = async (organizationData: {
    name: string;
    industry: string;
    size: string;
    website?: string;
    adminEmail: string;
    adminPassword: string;
    status?: "active" | "suspended" | "trial";
  }) => {
    try {
      setOrganizationCreateLoading(true);
      setOrganizationsError(null);

      console.log("Creating organization:", organizationData);

      // Convert UI data to API format
      const apiCreateData: any = {
        name: organizationData.name,
        industry: organizationData.industry,
        size: organizationData.size,
        website: organizationData.website,
        adminEmail: organizationData.adminEmail,
        adminPassword: organizationData.adminPassword,
        status:
          organizationData.status === "active"
            ? "ACTIVE"
            : organizationData.status === "suspended"
            ? "SUSPENDED"
            : organizationData.status === "trial"
            ? "INACTIVE"
            : "ACTIVE", // default to active
      };

      const response = await realAPI.superAdmin.createOrganization(
        apiCreateData
      );

      console.log("Organization create response:", response);

      if (response && response.success && response.data) {
        // Convert the created organization to UI format and add to state
        const newUIOrganization = convertAPIOrganizationToUIOrganization(
          response.data
        );
        setOrganizations((prevOrgs) => [newUIOrganization, ...prevOrgs]);

        setShowCreateOrg(false);
        console.log("Organization created successfully:", newUIOrganization);
        return { success: true, data: newUIOrganization };
      } else {
        const errorMessage =
          response?.error?.message || "Failed to create organization";
        setOrganizationsError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error("Error creating organization:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to create organization";
      setOrganizationsError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setOrganizationCreateLoading(false);
    }
  };

  // Progressive plan topup function
  const topupProgressivePlan = async (topupData: {
    organizationId: number;
    planId: number;
    amount: number;
    adminNote: string;
    adminEmail: string;
  }) => {
    try {
      setTopupLoading(true);
      setOrganizationsError(null);

      console.log("Topping up progressive plan:", topupData);

      const response = await realAPI.superAdmin.topupProgressivePlan(topupData);

      console.log("Topup response:", response);

      if (response && response.success) {
        // Refresh organizations to get updated balances
        await fetchOrganizations();
        setShowTopupModal(false);
        setTopupOrganization(null);
        return { success: true, data: response.data };
      } else {
        const errorMessage =
          response?.error?.message || "Failed to topup progressive plan";
        setOrganizationsError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error("Error topping up progressive plan:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to topup progressive plan";
      setOrganizationsError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setTopupLoading(false);
    }
  };

  // Support ticket states
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(
    null
  );
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [ticketSearchTerm, setTicketSearchTerm] = useState("");
  const [ticketFilterStatus, setTicketFilterStatus] = useState<
    "all" | "open" | "in-progress" | "resolved" | "closed"
  >("all");
  const [ticketFilterPriority, setTicketFilterPriority] = useState<
    "all" | "low" | "medium" | "high" | "urgent"
  >("all");
  const [ticketFilterCategory, setTicketFilterCategory] = useState<
    | "all"
    | "technical"
    | "billing"
    | "account"
    | "feature-request"
    | "bug"
    | "general"
  >("all");
  const [newComment, setNewComment] = useState("");
  const [isInternalComment, setIsInternalComment] = useState(false);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState<string | null>(null);
  const [ticketPage, setTicketPage] = useState(0);
  const ticketSize = 20;
  const [ticketTotalPages, setTicketTotalPages] = useState(0);
  const [ticketDetailsLoading, setTicketDetailsLoading] = useState(false);
  const [ticketDetailsError, setTicketDetailsError] = useState<string | null>(
    null
  );

  // Demo support tickets data
  const demoTickets: SupportTicket[] = [
    {
      id: "1",
      ticketNumber: "TICK-2025-001",
      subject: "Login Issues - Cannot Access Dashboard",
      description:
        "Users in our organization are experiencing intermittent login failures. The error occurs approximately 30% of the time when attempting to access the dashboard.",
      priority: "high",
      status: "open",
      category: "technical",
      organizationId: "1",
      organizationName: "TechCorp Solutions",
      userEmail: "admin@techcorp.com",
      userName: "John Smith",
      assignedTo: "support_001",
      assignedToName: "Sarah Wilson",
      createdAt: "2025-09-01T10:30:00Z",
      updatedAt: "2025-09-01T14:22:00Z",
      tags: ["login", "dashboard", "authentication"],
      attachments: ["screenshot.png"],
      comments: [
        {
          id: "c1",
          ticketId: "1",
          author: "John Smith",
          authorType: "user",
          message:
            "This issue started occurring yesterday after the maintenance window.",
          isInternal: false,
          createdAt: "2025-09-01T10:35:00Z",
          attachments: [],
        },
        {
          id: "c2",
          ticketId: "1",
          author: "Sarah Wilson",
          authorType: "support",
          message:
            "Thank you for reporting this issue. I'm investigating the authentication service logs.",
          isInternal: false,
          createdAt: "2025-09-01T14:22:00Z",
          attachments: [],
        },
      ],
    },
    {
      id: "2",
      ticketNumber: "TICK-2025-002",
      subject: "Billing Discrepancy - Wrong Plan Charged",
      description:
        "We were charged for the Enterprise plan but we're currently on the Professional plan. Please review our billing.",
      priority: "medium",
      status: "in-progress",
      category: "billing",
      organizationId: "2",
      organizationName: "StartupFlow Inc",
      userEmail: "billing@startupflow.com",
      userName: "Mike Johnson",
      assignedTo: "support_002",
      assignedToName: "David Chen",
      createdAt: "2025-08-30T09:15:00Z",
      updatedAt: "2025-09-01T11:45:00Z",
      tags: ["billing", "plan-change", "enterprise"],
      attachments: ["invoice.pdf"],
      comments: [
        {
          id: "c3",
          ticketId: "2",
          author: "David Chen",
          authorType: "support",
          message:
            "I've reviewed your account and confirmed the billing discrepancy. Processing a refund now.",
          isInternal: false,
          createdAt: "2025-09-01T11:45:00Z",
          attachments: [],
        },
      ],
    },
    {
      id: "3",
      ticketNumber: "TICK-2025-003",
      subject: "Feature Request - Dark Mode",
      description:
        "Our team would love to have a dark mode option for better user experience during night shifts.",
      priority: "low",
      status: "open",
      category: "feature-request",
      organizationId: "3",
      organizationName: "HealthTech Labs",
      userEmail: "product@healthtech.com",
      userName: "Emily Davis",
      createdAt: "2025-08-28T16:20:00Z",
      updatedAt: "2025-08-28T16:20:00Z",
      tags: ["feature-request", "ui", "dark-mode"],
      attachments: [],
      comments: [],
    },
    {
      id: "4",
      ticketNumber: "TICK-2025-004",
      subject: "Critical Bug - Data Loss in Sprint Planning",
      description:
        "We lost all our sprint planning data when switching between sprints. This is blocking our entire development team.",
      priority: "urgent",
      status: "resolved",
      category: "bug",
      organizationId: "1",
      organizationName: "TechCorp Solutions",
      userEmail: "dev@techcorp.com",
      userName: "Alex Rodriguez",
      assignedTo: "support_001",
      assignedToName: "Sarah Wilson",
      createdAt: "2025-08-25T08:45:00Z",
      updatedAt: "2025-08-26T14:30:00Z",
      resolvedAt: "2025-08-26T14:30:00Z",
      tags: ["bug", "data-loss", "sprint", "critical"],
      attachments: ["error-log.txt"],
      comments: [
        {
          id: "c4",
          ticketId: "4",
          author: "Sarah Wilson",
          authorType: "support",
          message:
            "We've restored your data from our backup systems and implemented additional safeguards.",
          isInternal: false,
          createdAt: "2025-08-26T14:30:00Z",
          attachments: [],
        },
      ],
    },
    {
      id: "5",
      ticketNumber: "TICK-2025-005",
      subject: "Account Setup Assistance",
      description:
        "Need help setting up team permissions and user roles for our new organization.",
      priority: "medium",
      status: "closed",
      category: "account",
      organizationId: "4",
      organizationName: "Consulting Pro",
      userEmail: "admin@consulting.com",
      userName: "Lisa Wang",
      assignedTo: "support_003",
      assignedToName: "Mark Thompson",
      createdAt: "2025-08-20T13:10:00Z",
      updatedAt: "2025-08-22T10:15:00Z",
      resolvedAt: "2025-08-22T10:15:00Z",
      tags: ["account-setup", "permissions", "onboarding"],
      attachments: [],
      comments: [
        {
          id: "c5",
          ticketId: "5",
          author: "Mark Thompson",
          authorType: "support",
          message:
            "I've helped set up your team structure and permissions. Everything should be working correctly now.",
          isInternal: false,
          createdAt: "2025-08-22T10:15:00Z",
          attachments: [],
        },
      ],
    },
  ];

  // Helpers to map backend enums to UI
  const mapStatusToUI = (s: any): SupportTicket["status"] => {
    if (!s) return "open";
    const v = String(s).toLowerCase().replace(/_/g, "-");
    return (v as SupportTicket["status"]) || "open";
  };
  const mapPriorityToUI = (p: any): SupportTicket["priority"] => {
    if (!p) return "medium";
    return (String(p).toLowerCase() as SupportTicket["priority"]) || "medium";
  };
  const mapCategoryToUI = (c: any): SupportTicket["category"] => {
    if (!c) return "general";
    const v = String(c).toLowerCase().replace(/_/g, "-");
    return (v as SupportTicket["category"]) || "general";
  };

  const toEnum = (v: string) => String(v).replace(/-/g, "_").toUpperCase();

  // Load tickets from API
  const loadTickets = async (page = 0) => {
    try {
      setTicketsLoading(true);
      setTicketsError(null);
      const status =
        ticketFilterStatus === "all" ? "" : toEnum(ticketFilterStatus);
      const priority =
        ticketFilterPriority === "all" ? "" : toEnum(ticketFilterPriority);
      const category =
        ticketFilterCategory === "all" ? "" : toEnum(ticketFilterCategory);
      const search = ticketSearchTerm.trim();
      const resp = await realAPI.superAdmin.getAllSupportTickets(
        page,
        ticketSize,
        status,
        priority,
        category,
        search
      );
      // Support multiple response shapes:
      // 1) { success, data: { tickets: [...], pagination: {...}, statistics: {...} } }
      // 2) { content: [...], totalPages, number }
      // 3) [ ... ]
      const pageObj: any = resp?.data ?? resp;
      let content: any[] = [];
      if (Array.isArray(pageObj?.tickets)) {
        content = pageObj.tickets;
      } else if (Array.isArray(resp?.tickets)) {
        content = resp.tickets;
      } else if (Array.isArray(pageObj?.content)) {
        content = pageObj.content;
      } else if (Array.isArray(resp?.content)) {
        content = resp.content;
      } else if (Array.isArray(resp?.data)) {
        content = resp.data;
      } else if (Array.isArray(pageObj)) {
        content = pageObj;
      }

      const mapped: SupportTicket[] = content.map((t: any) => ({
        id: (t.id ?? t.ticketId ?? t.uuid ?? "").toString(),
        ticketNumber: t.ticketNumber ?? t.number ?? t.code ?? "",
        subject: t.subject ?? t.title ?? "",
        description: t.description ?? "",
        priority: mapPriorityToUI(t.priority),
        status: mapStatusToUI(t.status),
        category: mapCategoryToUI(t.category ?? t.supportType),
        organizationId: (t.organizationId ?? t.orgId ?? "").toString(),
        organizationName: t.organizationName ?? t.orgName ?? "",
        userEmail: t.userEmail ?? t.email ?? "",
        userName: t.userName ?? t.requesterName ?? "",
        assignedTo: t.assignedTo ?? t.assigneeId ?? undefined,
        assignedToName: t.assignedToName ?? t.assigneeName ?? undefined,
        createdAt: t.createdAt ?? new Date().toISOString(),
        updatedAt:
          t.updatedAt ??
          t.lastUpdated ??
          t.createdAt ??
          new Date().toISOString(),
        resolvedAt: t.resolvedAt,
        tags: Array.isArray(t.tags) ? t.tags : [],
        attachments: Array.isArray(t.attachments) ? t.attachments : [],
        comments: Array.isArray(t.comments)
          ? t.comments.map((c: any) => ({
              id: (c.id ?? "").toString(),
              ticketId: (c.ticketId ?? t.id ?? "").toString(),
              author: c.authorName ?? c.author ?? "",
              authorType: (String(c.authorRole ?? "user").toLowerCase() ===
              "support"
                ? "support"
                : "user") as TicketComment["authorType"],
              message: c.content ?? c.message ?? "",
              isInternal: !!c.isInternal,
              createdAt: c.createdAt ?? new Date().toISOString(),
              attachments: Array.isArray(c.attachments) ? c.attachments : [],
            }))
          : [],
      }));

      setTickets(mapped);
      const pagination = pageObj?.pagination ?? resp?.pagination ?? null;
      const totalPages =
        pagination?.totalPages ?? pageObj?.totalPages ?? resp?.totalPages ?? 0;
      const currentPage =
        pagination?.currentPage ?? pageObj?.number ?? resp?.number ?? page;
      setTicketPage(currentPage);
      setTicketTotalPages(totalPages);
    } catch (e: any) {
      console.error("Failed to load tickets:", e);
      setTicketsError(e?.message || "Failed to load tickets");
      // Fallback to demo data if API fails
      setTickets(demoTickets);
      setTicketTotalPages(1);
    } finally {
      setTicketsLoading(false);
    }
  };

  // Load when tab opens
  useEffect(() => {
    if (activeTab === "tickets") {
      loadTickets(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Load organizations when component mounts or organizations tab is accessed
  useEffect(() => {
    if (activeTab === "organizations") {
      fetchOrganizations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Also fetch organizations on component mount for the overview metrics
  useEffect(() => {
    fetchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize plans with demo data
  useEffect(() => {
    setLoading(false);
    setError(null);
    setPlans([
      {
        id: 1,
        key: "free",
        name: "Free",
        description: "Perfect for small teams getting started",
        isMostPopular: false,
        billingOptions: [
          {
            id: 1,
            billingCycle: "MONTHLY" as const,
            priceCents: 0,
            formattedPrice: "$0",
            isDefault: true,
            savingsPercentage: null,
          },
        ],
        features: [
          {
            featureId: 1,
            featureName: "Users",
            featureCode: "users",
            value: "Up to 5",
          },
          {
            featureId: 2,
            featureName: "Stories",
            featureCode: "stories",
            value: "50 stories",
          },
          {
            featureId: 3,
            featureName: "Support",
            featureCode: "support",
            value: "Basic support",
          },
        ],
        limits: {
          maxUsers: 5,
          maxTeams: 1,
          storageBytes: 1073741824,
          formattedStorage: "1 GB",
          supportType: "Basic",
        },
        organizationCount: 1250,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: 2,
        key: "pro",
        name: "Pro",
        description: "For growing teams that need more capacity",
        isMostPopular: true,
        billingOptions: [
          {
            id: 2,
            billingCycle: "MONTHLY" as const,
            priceCents: 2900,
            formattedPrice: "$29",
            isDefault: true,
            savingsPercentage: null,
          },
        ],
        features: [
          {
            featureId: 1,
            featureName: "Users",
            featureCode: "users",
            value: "Up to 25",
          },
          {
            featureId: 2,
            featureName: "Stories",
            featureCode: "stories",
            value: "500 stories",
          },
          {
            featureId: 3,
            featureName: "Support",
            featureCode: "support",
            value: "Priority support",
          },
          {
            featureId: 4,
            featureName: "Analytics",
            featureCode: "analytics",
            value: "Advanced analytics",
          },
        ],
        limits: {
          maxUsers: 25,
          maxTeams: 5,
          storageBytes: 10737418240,
          formattedStorage: "10 GB",
          supportType: "Priority",
        },
        organizationCount: 340,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: 3,
        key: "enterprise",
        name: "Enterprise",
        description: "For large organizations with custom needs",
        isMostPopular: false,
        billingOptions: [
          {
            id: 3,
            billingCycle: "MONTHLY" as const,
            priceCents: 9900,
            formattedPrice: "$99",
            isDefault: true,
            savingsPercentage: null,
          },
        ],
        features: [
          {
            featureId: 1,
            featureName: "Users",
            featureCode: "users",
            value: "Unlimited",
          },
          {
            featureId: 2,
            featureName: "Stories",
            featureCode: "stories",
            value: "Unlimited stories",
          },
          {
            featureId: 3,
            featureName: "Support",
            featureCode: "support",
            value: "24/7 support",
          },
          {
            featureId: 4,
            featureName: "Integrations",
            featureCode: "integrations",
            value: "Custom integrations",
          },
        ],
        limits: {
          maxUsers: -1,
          maxTeams: -1,
          storageBytes: null,
          formattedStorage: "Unlimited",
          supportType: "24/7 Premium",
        },
        organizationCount: 45,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ]);
  }, []);

  // Load progressive plans from real API
  const loadProgressivePlans = async () => {
    try {
      setProgressivePlansLoading(true);
      setProgressivePlansError(null);
      const response: ProgressivePlansResponse =
        await realAPI.superAdmin.getAllProgressivePlans();
      if (response.success && response.data?.plans) {
        setProgressivePlans(response.data.plans);
      } else {
        setProgressivePlansError("Failed to load progressive plans");
      }
    } catch (error) {
      console.error("Error loading progressive plans:", error);
      setProgressivePlansError("Failed to load progressive plans");
    } finally {
      setProgressivePlansLoading(false);
    }
  };

  // Initialize progressive plans
  useEffect(() => {
    loadProgressivePlans();
  }, []);

  // Update plan function (demo mode)
  const updatePlan = async (planId: number, planData: PlanUpdateRequest) => {
    try {
      setUpdateLoading(true);
      setError(null);

      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Update plan in local state
      setPlans((prevPlans) =>
        prevPlans.map((plan) =>
          plan.id === planId
            ? {
                ...plan,
                name: planData.name || plan.name,
                description: planData.description || plan.description,
                updatedAt: new Date().toISOString(),
              }
            : plan
        )
      );

      setEditingPlan(null);
      return { success: true };
    } catch (err) {
      const errorMessage = "Error updating plan: " + (err as Error).message;
      setError(errorMessage);
      console.error("Error updating plan:", err);
      return { success: false, error: errorMessage };
    } finally {
      setUpdateLoading(false);
    }
  };

  // Handle plan edit form submission
  const handlePlanEdit = async (formData: FormData) => {
    if (!editingPlan) return;

    const planData: PlanUpdateRequest = {
      name: formData.get("name") as string,
      description: formData.get("description") as string,
      isMostPopular: formData.get("isMostPopular") === "true",
      billingOptions: editingPlan.billingOptions.map((billing, index) => ({
        id: billing.id,
        billingCycle: billing.billingCycle,
        priceCents:
          parseInt(formData.get(`billing_${index}_priceCents`) as string) ||
          billing.priceCents,
        isDefault: formData.get(`billing_${index}_isDefault`) === "true",
        savingsPercentage: billing.savingsPercentage,
      })),
      features: editingPlan.features.map((feature, index) => ({
        featureId: feature.featureId,
        featureName:
          (formData.get(`feature_${index}_name`) as string) ||
          feature.featureName,
        featureCode: feature.featureCode,
        value:
          (formData.get(`feature_${index}_value`) as string) || feature.value,
      })),
      limits: {
        maxUsers:
          parseInt(formData.get("maxUsers") as string) ||
          editingPlan.limits.maxUsers,
        maxTeams:
          parseInt(formData.get("maxTeams") as string) ||
          editingPlan.limits.maxTeams,
        storageBytes: editingPlan.limits.storageBytes,
        supportType:
          (formData.get("supportType") as string) ||
          editingPlan.limits.supportType,
      },
    };

    const result = await updatePlan(editingPlan.id, planData);
    return result;
  };

  // Progressive Plans management functions
  const handleCreateProgressivePlan = async (
    planData: Omit<ProgressivePricingPlan, "id" | "createdAt" | "updatedAt">
  ) => {
    try {
      setProgressivePlansLoading(true);
      setProgressivePlansError(null);
      await realAPI.superAdmin.createProgressivePlan(planData);
      await loadProgressivePlans(); // Reload the list
      setShowCreateProgressivePlan(false);
    } catch (error) {
      console.error("Error creating progressive plan:", error);
      setProgressivePlansError("Failed to create progressive plan");
    } finally {
      setProgressivePlansLoading(false);
    }
  };

  const handleUpdateProgressivePlan = async (
    id: number,
    planData: Partial<ProgressivePricingPlan>
  ) => {
    try {
      setProgressivePlansLoading(true);
      setProgressivePlansError(null);
      await realAPI.superAdmin.updateProgressivePlan(id, planData);
      await loadProgressivePlans(); // Reload the list
      setEditingProgressivePlan(null);
    } catch (error) {
      console.error("Error updating progressive plan:", error);
      setProgressivePlansError("Failed to update progressive plan");
    } finally {
      setProgressivePlansLoading(false);
    }
  };

  const handleDeleteProgressivePlan = async (id: number) => {
    if (
      !window.confirm(
        "Are you sure you want to deactivate this progressive plan? This will make it unavailable for new subscriptions."
      )
    ) {
      return;
    }

    try {
      setProgressivePlansLoading(true);
      setProgressivePlansError(null);

      // Find the plan to deactivate
      const plan = progressivePlans.find((p) => p.id === id);
      if (!plan) {
        throw new Error("Plan not found");
      }

      // Update the plan to set isActive to false
      await realAPI.superAdmin.updateProgressivePlan(id, {
        ...plan,
        isActive: false,
      });

      await loadProgressivePlans(); // Reload the list
    } catch (error) {
      console.error("Error deactivating progressive plan:", error);
      setProgressivePlansError("Failed to deactivate progressive plan");
    } finally {
      setProgressivePlansLoading(false);
    }
  };

  const handleActivateProgressivePlan = async (id: number) => {
    if (
      !window.confirm(
        "Are you sure you want to activate this progressive plan? This will make it available for new subscriptions."
      )
    ) {
      return;
    }

    try {
      setProgressivePlansLoading(true);
      setProgressivePlansError(null);

      // Find the plan to activate
      const plan = progressivePlans.find((p) => p.id === id);
      if (!plan) {
        throw new Error("Plan not found");
      }

      // Update the plan to set isActive to true
      await realAPI.superAdmin.updateProgressivePlan(id, {
        ...plan,
        isActive: true,
      });

      await loadProgressivePlans(); // Reload the list
    } catch (error) {
      console.error("Error activating progressive plan:", error);
      setProgressivePlansError("Failed to activate progressive plan");
    } finally {
      setProgressivePlansLoading(false);
    }
  };

  // Calculate system metrics from real organization data
  const metrics: SystemMetrics = React.useMemo(() => {
    const totalUsers = organizations.reduce((sum, org) => sum + org.users, 0);
    const totalRevenue = organizations.reduce(
      (sum, org) => sum + org.monthlyRevenue,
      0
    );
    const totalTeams = organizations.reduce((sum, org) => sum + org.teams, 0);
    const activeTrials = organizations.filter(
      (org) => org.status === "trial"
    ).length;
    const averageTeamsPerOrg =
      organizations.length > 0 ? totalTeams / organizations.length : 0;

    return {
      totalOrganizations: organizations.length,
      totalUsers,
      totalRevenue,
      activeTrials,
      conversionRate: 23.5, // Keep as static for now
      churnRate: 4.2, // Keep as static for now
      averageTeamsPerOrg: Math.round(averageTeamsPerOrg * 10) / 10,
      supportTickets: 24, // Keep as static for now
    };
  }, [organizations]);

  // Filter organizations based on search and filters
  const filteredOrganizations = organizations.filter((org) => {
    const matchesSearch =
      !searchTerm ||
      org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.industry.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.adminEmail.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === "all" || org.status === filterStatus;
    const matchesPlan = filterPlan === "all" || org.plan === filterPlan;

    return matchesSearch && matchesStatus && matchesPlan;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusBadge = (status: Organization["status"]) => {
    const styles = {
      active: "bg-green-100 text-green-800",
      trial: "bg-blue-100 text-blue-800",
      suspended: "bg-red-100 text-red-800",
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getPlanBadge = (planKey: string) => {
    const styles = {
      free: "bg-gray-100 text-gray-800",
      professional: "bg-purple-100 text-purple-800",
      enterprise: "bg-yellow-100 text-yellow-800",
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          styles[planKey as keyof typeof styles] || "bg-gray-100 text-gray-800"
        }`}
      >
        {planKey.charAt(0).toUpperCase() + planKey.slice(1)}
      </span>
    );
  };

  // Rest of the component implementation (overview, organizations, plans, analytics, settings tabs)
  // This is kept as demo data for reference and testing

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Organizations</p>
              <p className="text-2xl font-bold text-gray-900">
                {metrics.totalOrganizations.toLocaleString()}
              </p>
            </div>
            <Building2 className="h-8 w-8 text-blue-600" />
          </div>
          <p className="text-xs text-gray-500 mt-2">+12% from last month</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">
                {metrics.totalUsers.toLocaleString()}
              </p>
            </div>
            <Users className="h-8 w-8 text-green-600" />
          </div>
          <p className="text-xs text-gray-500 mt-2">+8% from last month</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Monthly Revenue
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(metrics.totalRevenue)}
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-purple-600" />
          </div>
          <p className="text-xs text-gray-500 mt-2">+15% from last month</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Trials</p>
              <p className="text-2xl font-bold text-gray-900">
                {metrics.activeTrials}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-orange-600" />
          </div>
          <p className="text-xs text-gray-500 mt-2">+5% from last month</p>
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Conversion Rate
              </p>
              <p className="text-2xl font-bold text-green-600">
                {metrics.conversionRate}%
              </p>
            </div>
            <UserCheck className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Churn Rate</p>
              <p className="text-2xl font-bold text-red-600">
                {metrics.churnRate}%
              </p>
            </div>
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Teams/Org</p>
              <p className="text-2xl font-bold text-blue-600">
                {metrics.averageTeamsPerOrg}
              </p>
            </div>
            <Activity className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Support Tickets
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {metrics.supportTickets}
              </p>
            </div>
            <Shield className="h-8 w-8 text-gray-600" />
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Recent Activity
          </h3>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <Building2 className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-900">
                  New organization <strong>TechCorp Solutions</strong> signed up
                  for Enterprise plan
                </p>
                <p className="text-xs text-gray-500">2 hours ago</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-orange-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-900">
                  <strong>Manufacturing Inc</strong> account suspended due to
                  payment failure
                </p>
                <p className="text-xs text-gray-500">6 hours ago</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-900">
                  <strong>Design Studio Pro</strong> upgraded from Free to
                  Professional
                </p>
                <p className="text-xs text-gray-500">1 day ago</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderOrganizations = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Organizations</h2>
          <p className="text-sm text-gray-600">
            Manage all organizations and their subscriptions
          </p>
        </div>
        <div className="flex gap-2 mt-4 sm:mt-0">
          <button
            onClick={fetchOrganizations}
            disabled={organizationsLoading}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <Activity className="h-4 w-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={() => setShowCreateOrg(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Organization
          </button>
        </div>
      </div>

      {/* Loading State */}
      {organizationsLoading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading organizations...</p>
        </div>
      )}

      {/* Error State */}
      {organizationsError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{organizationsError}</p>
              <button
                onClick={fetchOrganizations}
                className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters - Only show when not loading */}
      {!organizationsLoading && !organizationsError && (
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search organizations..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                value={filterStatus}
                onChange={(e) =>
                  setFilterStatus(
                    e.target.value as "all" | "active" | "suspended" | "trial"
                  )
                }
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="trial">Trial</option>
                <option value="suspended">Suspended</option>
              </select>
              <select
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                value={filterPlan}
                onChange={(e) =>
                  setFilterPlan(
                    e.target.value as
                      | "all"
                      | "free"
                      | "professional"
                      | "enterprise"
                  )
                }
              >
                <option value="all">All Plans</option>
                <option value="free">Free</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Organizations Table - Only show when not loading and no error */}
      {!organizationsLoading && !organizationsError && (
        <div className="bg-white shadow-sm border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Users
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progressive Balance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Active
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrganizations.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-6 py-12 text-center text-sm text-gray-500"
                    >
                      {organizations.length === 0
                        ? "No organizations found"
                        : "No organizations match your search criteria"}
                    </td>
                  </tr>
                ) : (
                  filteredOrganizations.map((org) => (
                    <tr key={org.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {org.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {org.industry} • {org.size}
                          </div>
                          <div className="text-sm text-gray-500">
                            {org.adminEmail || "No admin email"}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getPlanBadge(org.plan)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(org.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>{org.users} users</div>
                        <div className="text-gray-500">{org.teams} teams</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {org.monthlyRevenue > 0
                          ? formatCurrency(org.monthlyRevenue)
                          : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          {org.progressivePlanBalances &&
                          org.progressivePlanBalances.length > 0 ? (
                            org.progressivePlanBalances.map(
                              (balance, index) => (
                                <div key={index} className="text-sm">
                                  <div className="text-gray-900">
                                    <span
                                      className={`font-medium px-2 py-1 rounded text-xs ${
                                        balance.progressivePricingType ===
                                        "STORIES"
                                          ? "bg-blue-100 text-blue-800"
                                          : balance.progressivePricingType ===
                                            "USERS"
                                          ? "bg-green-100 text-green-800"
                                          : "bg-gray-100 text-gray-800"
                                      }`}
                                    >
                                      {balance.progressivePricingType}
                                    </span>{" "}
                                    <span className="ml-1 font-medium">
                                      {balance.balance}/{balance.quota}
                                    </span>
                                  </div>
                                  {balance.isDefault && (
                                    <div className="text-xs text-gray-500">
                                      Default • {balance.planKey}
                                    </div>
                                  )}
                                </div>
                              )
                            )
                          ) : (
                            <div className="text-sm text-gray-500">
                              No progressive plans
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(org.lastActive)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => setSelectedOrganization(org)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Organization"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setEditingOrganization(org)}
                            className="text-gray-600 hover:text-gray-900"
                            title="Edit Organization"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setTopupOrganization(org);
                              setShowTopupModal(true);
                            }}
                            className="text-green-600 hover:text-green-900"
                            title="Topup Progressive Plan"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Are you sure you want to delete "${org.name}"? This action cannot be undone.`
                                )
                              ) {
                                // TODO: Implement delete functionality
                                console.log("Delete organization:", org.id);
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Organization"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  const renderPlans = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">
            Plans Management
          </h2>
          <p className="text-sm text-gray-600">
            Manage subscription plans and pricing
          </p>
        </div>
        <button className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-2" />
          Create Plan
        </button>
      </div>

      {/* Loading and Error States */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading plans...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Plans Grid */}
      {!loading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan) => {
            // Get the default billing option for display
            const defaultBilling =
              plan.billingOptions.find((option) => option.isDefault) ||
              plan.billingOptions[0];
            const price = defaultBilling ? defaultBilling.priceCents / 100 : 0;
            const billingCycle = defaultBilling
              ? defaultBilling.billingCycle.toLowerCase()
              : "monthly";

            return (
              <div
                key={plan.id}
                className={`relative bg-white rounded-lg shadow-sm border-2 ${
                  plan.isMostPopular ? "border-purple-500" : "border-gray-200"
                }`}
              >
                {plan.isMostPopular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="p-6">
                  <div className="text-center mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {plan.name}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {plan.description}
                    </p>
                    <div className="mt-2">
                      <span className="text-3xl font-bold text-gray-900">
                        ${price}
                      </span>
                      <span className="text-gray-600">/{billingCycle}</span>
                    </div>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <div className="flex-shrink-0 mt-1">
                          <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                        </div>
                        <span className="ml-3 text-sm text-gray-600">
                          {feature.value}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="border-t pt-4">
                    <div className="text-xs text-gray-500 space-y-1">
                      <div>
                        Up to{" "}
                        {plan.limits.maxUsers === 999999
                          ? "unlimited"
                          : plan.limits.maxUsers}{" "}
                        users
                      </div>
                      <div>
                        {plan.limits.maxTeams === 999999
                          ? "Unlimited"
                          : plan.limits.maxTeams}{" "}
                        teams
                      </div>
                      <div>{plan.limits.formattedStorage} storage</div>
                      <div>{plan.limits.supportType}</div>
                    </div>
                  </div>
                  <div className="mt-4 flex space-x-2">
                    <button
                      onClick={() => setEditingPlan(plan)}
                      className="flex-1 text-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setSelectedPlan(plan)}
                      className="flex-1 text-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
                    >
                      View Details
                    </button>
                  </div>
                  {plan.organizationCount > 0 && (
                    <div className="mt-2 text-center">
                      <span className="text-xs text-gray-500">
                        {plan.organizationCount} organization
                        {plan.organizationCount !== 1 ? "s" : ""} using this
                        plan
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderProgressivePlans = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">
            Progressive Plans
          </h2>
          <p className="text-sm text-gray-600">
            Manage progressive pricing plans for users and stories
          </p>
        </div>
        <button
          onClick={() => setShowCreateProgressivePlan(true)}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Progressive Plan
        </button>
      </div>

      {/* Error Display */}
      {progressivePlansError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-3 mt-0.5" />
            <div className="text-sm text-red-700">{progressivePlansError}</div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {progressivePlansLoading ? (
        <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading progressive plans...</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Current Progressive Plans
            </h3>
          </div>

          {progressivePlans.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No progressive plans found. Create one to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plan Key
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tier
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Limit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {progressivePlans.map((plan) => (
                    <tr
                      key={plan.id}
                      className={`hover:bg-gray-50 ${
                        !plan.isActive ? "opacity-60 bg-gray-25" : ""
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center">
                          {plan.planKey}
                          {!plan.isActive && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              Inactive
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            plan.type === ProgressivePricingType.STORIES
                              ? "bg-blue-100 text-blue-800"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          {plan.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            plan.tire === ProgressivePricingTire.FREE
                              ? "bg-gray-100 text-gray-800"
                              : "bg-purple-100 text-purple-800"
                          }`}
                        >
                          {plan.tire}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {plan.planLimit}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {plan.priceCents === 0
                          ? "Free"
                          : `$${(plan.priceCents / 100).toFixed(2)}`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            plan.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {plan.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setEditingProgressivePlan(plan)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit plan"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          {plan.isActive ? (
                            <button
                              onClick={() =>
                                handleDeleteProgressivePlan(plan.id)
                              }
                              className="text-orange-600 hover:text-orange-900"
                              title="Deactivate plan"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() =>
                                handleActivateProgressivePlan(plan.id)
                              }
                              className="text-green-600 hover:text-green-900"
                              title="Activate plan"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create Progressive Plan Modal */}
      {showCreateProgressivePlan && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Create Progressive Plan
              </h3>
              <button
                onClick={() => setShowCreateProgressivePlan(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const planData = {
                  planKey: formData.get("planKey") as string,
                  tire: formData.get("tire") as ProgressivePricingTire,
                  type: formData.get("type") as ProgressivePricingType,
                  planLimit: parseInt(formData.get("planLimit") as string),
                  isActive: formData.get("isActive") === "on",
                  priceCents:
                    parseInt(formData.get("priceCents") as string) || 0,
                  effectiveDate: new Date().toISOString(),
                };
                handleCreateProgressivePlan(planData);
              }}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Plan Key
                  </label>
                  <input
                    name="planKey"
                    type="text"
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., STORIES_TOPUP_50"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Type
                    </label>
                    <select
                      name="type"
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select type...</option>
                      {Object.values(ProgressivePricingType).map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Tier
                    </label>
                    <select
                      name="tire"
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select tier...</option>
                      {Object.values(ProgressivePricingTire).map((tire) => (
                        <option key={tire} value={tire}>
                          {tire}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Plan Limit
                    </label>
                    <input
                      name="planLimit"
                      type="number"
                      required
                      min="1"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., 50"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Price (cents)
                    </label>
                    <input
                      name="priceCents"
                      type="number"
                      min="0"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., 1000 (for $10.00)"
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    name="isActive"
                    type="checkbox"
                    defaultChecked
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-700">
                    Active
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateProgressivePlan(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  Create Plan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Progressive Plan Modal */}
      {editingProgressivePlan && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Edit Progressive Plan
              </h3>
              <button
                onClick={() => setEditingProgressivePlan(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const planData = {
                  planKey: formData.get("planKey") as string,
                  tire: formData.get("tire") as ProgressivePricingTire,
                  type: formData.get("type") as ProgressivePricingType,
                  planLimit: parseInt(formData.get("planLimit") as string),
                  isActive: formData.get("isActive") === "on",
                  priceCents:
                    parseInt(formData.get("priceCents") as string) || 0,
                  effectiveDate:
                    (formData.get("effectiveDate") as string) ||
                    new Date().toISOString(),
                };
                handleUpdateProgressivePlan(
                  editingProgressivePlan.id,
                  planData
                );
              }}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Plan Key
                  </label>
                  <input
                    name="planKey"
                    type="text"
                    required
                    defaultValue={editingProgressivePlan.planKey}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Type
                    </label>
                    <select
                      name="type"
                      required
                      defaultValue={editingProgressivePlan.type}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {Object.values(ProgressivePricingType).map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Tier
                    </label>
                    <select
                      name="tire"
                      required
                      defaultValue={editingProgressivePlan.tire}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {Object.values(ProgressivePricingTire).map((tire) => (
                        <option key={tire} value={tire}>
                          {tire}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Plan Limit
                    </label>
                    <input
                      name="planLimit"
                      type="number"
                      required
                      min="1"
                      defaultValue={editingProgressivePlan.planLimit}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Price (cents)
                    </label>
                    <input
                      name="priceCents"
                      type="number"
                      min="0"
                      defaultValue={editingProgressivePlan.priceCents}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Effective Date
                  </label>
                  <input
                    name="effectiveDate"
                    type="datetime-local"
                    defaultValue={new Date(editingProgressivePlan.effectiveDate)
                      .toISOString()
                      .slice(0, 16)}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    name="isActive"
                    type="checkbox"
                    defaultChecked={editingProgressivePlan.isActive}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-700">
                    Active
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setEditingProgressivePlan(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  Update Plan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  const renderAnalytics = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Analytics</h2>
          <p className="text-sm text-gray-600">
            System performance and business metrics
          </p>
        </div>
        <button className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
          <Download className="h-4 w-4 mr-2" />
          Export Report
        </button>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Revenue Trends
        </h3>
        <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
          <div className="text-center">
            <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">Revenue chart would go here</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            User Growth
          </h3>
          <div className="h-48 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
            <div className="text-center">
              <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">User growth chart</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Plan Distribution
          </h3>
          <div className="h-48 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
            <div className="text-center">
              <Activity className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">Plan distribution chart</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-900">System Settings</h2>
        <p className="text-sm text-gray-600">
          Configure system-wide settings and preferences
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            General Settings
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                System Name
              </label>
              <input
                type="text"
                defaultValue="Kanbello"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Default Trial Period (days)
              </label>
              <input
                type="number"
                defaultValue="14"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Support Email
              </label>
              <input
                type="email"
                defaultValue="support@kanbello.com"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Billing Settings
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Payment Gateway
              </label>
              <select className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500">
                <option>Stripe</option>
                <option>PayPal</option>
                <option>Square</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Currency
              </label>
              <select className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500">
                <option>USD</option>
                <option>EUR</option>
                <option>GBP</option>
              </select>
            </div>
            <div className="flex items-center">
              <input type="checkbox" className="mr-2" defaultChecked />
              <label className="text-sm text-gray-700">
                Enable automatic billing
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Support ticket helper functions
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "bg-red-100 text-red-800";
      case "high":
        return "bg-orange-100 text-orange-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "open":
        return "bg-blue-100 text-blue-800";
      case "in-progress":
        return "bg-purple-100 text-purple-800";
      case "resolved":
        return "bg-green-100 text-green-800";
      case "closed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "technical":
        return <Shield className="h-4 w-4" />;
      case "billing":
        return <DollarSign className="h-4 w-4" />;
      case "account":
        return <User className="h-4 w-4" />;
      case "feature-request":
        return <Plus className="h-4 w-4" />;
      case "bug":
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  const filteredTickets = tickets.filter((ticket) => {
    const matchesSearch =
      ticket.subject.toLowerCase().includes(ticketSearchTerm.toLowerCase()) ||
      ticket.ticketNumber
        .toLowerCase()
        .includes(ticketSearchTerm.toLowerCase()) ||
      ticket.organizationName
        .toLowerCase()
        .includes(ticketSearchTerm.toLowerCase()) ||
      ticket.userEmail.toLowerCase().includes(ticketSearchTerm.toLowerCase());
    const matchesStatus =
      ticketFilterStatus === "all" || ticket.status === ticketFilterStatus;
    const matchesPriority =
      ticketFilterPriority === "all" ||
      ticket.priority === ticketFilterPriority;
    const matchesCategory =
      ticketFilterCategory === "all" ||
      ticket.category === ticketFilterCategory;

    return matchesSearch && matchesStatus && matchesPriority && matchesCategory;
  });

  const handleTicketStatusUpdate = async (
    ticketId: string,
    newStatus: SupportTicket["status"]
  ) => {
    // Optimistic UI update
    const prev = tickets;
    const updated = tickets.map((t) =>
      t.id === ticketId
        ? {
            ...t,
            status: newStatus,
            updatedAt: new Date().toISOString(),
            ...(newStatus === "resolved" && {
              resolvedAt: new Date().toISOString(),
            }),
          }
        : t
    );
    setTickets(updated);
    if (selectedTicket && selectedTicket.id === ticketId) {
      setSelectedTicket({
        ...selectedTicket,
        status: newStatus,
        updatedAt: new Date().toISOString(),
      });
    }
    try {
      await realAPI.superAdmin.updateTicketStatus(ticketId, toEnum(newStatus));
    } catch (e) {
      console.error("Failed to update status:", e);
      // Revert on failure
      setTickets(prev);
      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket(
          prev.find((t) => t.id === ticketId) || selectedTicket
        );
      }
      alert((e as any)?.message || "Failed to update status");
    }
  };

  const handleAssignTicket = (
    ticketId: string,
    assignedTo: string,
    assignedToName: string
  ) => {
    // Update local state only (demo mode)
    setTickets(
      tickets.map((ticket) =>
        ticket.id === ticketId
          ? {
              ...ticket,
              assignedTo,
              assignedToName,
              updatedAt: new Date().toISOString(),
            }
          : ticket
      )
    );
    if (selectedTicket && selectedTicket.id === ticketId) {
      setSelectedTicket({
        ...selectedTicket,
        assignedTo,
        assignedToName,
        updatedAt: new Date().toISOString(),
      });
    }
  };

  const handleAddComment = async (
    ticketId: string,
    message: string,
    isInternal: boolean = false
  ) => {
    if (!message.trim()) return;
    try {
      const resp = await realAPI.superAdmin.addTicketComment(ticketId, {
        message: message.trim(),
        isInternal,
      });
      const created: TicketComment = resp?.data || resp;
      // Update list
      setTickets((prev) =>
        prev.map((t) =>
          t.id === ticketId
            ? {
                ...t,
                // Append to keep oldest-first ordering
                comments: [...t.comments, created],
                updatedAt: new Date().toISOString(),
              }
            : t
        )
      );
      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket({
          ...selectedTicket,
          // Append to keep oldest-first ordering
          comments: [...selectedTicket.comments, created],
          updatedAt: new Date().toISOString(),
        });
      }
      setNewComment("");
      setIsInternalComment(false);
    } catch (e: any) {
      console.error("Failed to add comment:", e);
      alert(e?.message || "Failed to add comment");
    }
  };

  const renderSupportTickets = () => (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Support Tickets</h2>
          <p className="text-sm text-gray-600">
            Manage customer support tickets and requests
          </p>
        </div>
        <button
          onClick={() => setShowCreateTicket(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Ticket
        </button>
      </div>
      <div className="flex items-center justify-between">
        {ticketsError && (
          <div className="text-sm text-red-600">{ticketsError}</div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => loadTickets(ticketPage)}
            className="text-sm px-3 py-2 border rounded hover:bg-gray-50"
            disabled={ticketsLoading}
          >
            {ticketsLoading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Ticket Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <Ticket className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-xl font-semibold">{tickets.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Open</p>
              <p className="text-xl font-semibold">
                {tickets.filter((t) => t.status === "open").length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <Activity className="h-8 w-8 text-purple-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">In Progress</p>
              <p className="text-xl font-semibold">
                {tickets.filter((t) => t.status === "in-progress").length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Resolved</p>
              <p className="text-xl font-semibold">
                {tickets.filter((t) => t.status === "resolved").length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <AlertTriangle className="h-8 w-8 text-red-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Urgent</p>
              <p className="text-xl font-semibold">
                {tickets.filter((t) => t.priority === "urgent").length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search tickets..."
                value={ticketSearchTerm}
                onChange={(e) => setTicketSearchTerm(e.target.value)}
                className="pl-10 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={ticketFilterStatus}
              onChange={(e) => setTicketFilterStatus(e.target.value as any)}
              className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="open">Open</option>
              <option value="in-progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              value={ticketFilterPriority}
              onChange={(e) => setTicketFilterPriority(e.target.value as any)}
              className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={ticketFilterCategory}
              onChange={(e) => setTicketFilterCategory(e.target.value as any)}
              className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Categories</option>
              <option value="technical">Technical</option>
              <option value="billing">Billing</option>
              <option value="account">Account</option>
              <option value="feature-request">Feature Request</option>
              <option value="bug">Bug</option>
              <option value="general">General</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setTicketSearchTerm("");
                setTicketFilterStatus("all");
                setTicketFilterPriority("all");
                setTicketFilterCategory("all");
              }}
              className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ticket
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assigned To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(ticketsLoading ? [] : filteredTickets).map((ticket) => (
                <tr key={ticket.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {getCategoryIcon(ticket.category)}
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          #{ticket.ticketNumber}
                        </div>
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {ticket.subject}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {ticket.organizationName}
                    </div>
                    <div className="text-sm text-gray-500">
                      {ticket.userEmail}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(
                        ticket.priority
                      )}`}
                    >
                      {ticket.priority}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                        ticket.status
                      )}`}
                    >
                      {ticket.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 capitalize">
                      {ticket.category.replace("-", " ")}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {ticket.assignedToName || "Unassigned"}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {new Date(ticket.createdAt).toLocaleDateString()}
                    </div>
                    <div className="text-sm text-gray-500">
                      {new Date(ticket.createdAt).toLocaleTimeString()}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={async () => {
                          setTicketDetailsError(null);
                          setTicketDetailsLoading(true);
                          try {
                            const d =
                              await realAPI.superAdmin.getSupportTicketById(
                                ticket.id
                              );
                            const ticketDetail = (d?.data ?? d) as any;
                            const mappedDetail: SupportTicket = {
                              ...ticket,
                              subject: ticketDetail.subject ?? ticket.subject,
                              description:
                                ticketDetail.description ?? ticket.description,
                              priority: mapPriorityToUI(
                                ticketDetail.priority ?? ticket.priority
                              ),
                              status: mapStatusToUI(
                                ticketDetail.status ?? ticket.status
                              ),
                              category: mapCategoryToUI(
                                ticketDetail.category ?? ticket.category
                              ),
                              organizationName:
                                ticketDetail.organizationName ??
                                ticket.organizationName,
                              userEmail:
                                ticketDetail.userEmail ?? ticket.userEmail,
                              userName:
                                ticketDetail.userName ?? ticket.userName,
                            };
                            const cr =
                              await realAPI.superAdmin.getTicketComments(
                                ticket.id
                              );
                            const comments = (cr?.data ??
                              cr) as TicketComment[];
                            mappedDetail.comments = comments;
                            setSelectedTicket(mappedDetail);
                            setShowTicketModal(true);
                          } catch (e: any) {
                            setTicketDetailsError(
                              e?.message || "Failed to load ticket details"
                            );
                          } finally {
                            setTicketDetailsLoading(false);
                          }
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        View
                      </button>
                      <select
                        value={ticket.status}
                        onChange={(e) =>
                          handleTicketStatusUpdate(
                            ticket.id,
                            e.target.value as SupportTicket["status"]
                          )
                        }
                        className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="open">Open</option>
                        <option value="in-progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!ticketsLoading && filteredTickets.length === 0 && (
          <div className="text-center py-8">
            <Ticket className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              No tickets found matching your criteria.
            </p>
          </div>
        )}
        {ticketsLoading && (
          <div className="text-center py-8 text-gray-500">Loading tickets…</div>
        )}
      </div>

      {/* Pagination */}
      {ticketTotalPages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <button
            className="px-3 py-1 text-sm border rounded disabled:opacity-50"
            onClick={() => ticketPage > 0 && loadTickets(ticketPage - 1)}
            disabled={ticketsLoading || ticketPage === 0}
          >
            Previous
          </button>
          <div className="text-sm text-gray-600">
            Page {ticketPage + 1} of {ticketTotalPages}
          </div>
          <button
            className="px-3 py-1 text-sm border rounded disabled:opacity-50"
            onClick={() =>
              ticketPage + 1 < ticketTotalPages && loadTickets(ticketPage + 1)
            }
            disabled={ticketsLoading || ticketPage + 1 >= ticketTotalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* Ticket Detail Modal */}
      {showTicketModal && selectedTicket && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  #{selectedTicket.ticketNumber}
                </h3>
                <p className="text-gray-600 mt-1">{selectedTicket.subject}</p>
              </div>
              <button
                onClick={() => {
                  setShowTicketModal(false);
                  setSelectedTicket(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Ticket Details */}
              <div className="lg:col-span-2 space-y-6">
                {ticketDetailsError && (
                  <div className="p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm">
                    {ticketDetailsError}
                  </div>
                )}
                {ticketDetailsLoading && (
                  <div className="text-sm text-gray-500">Loading details…</div>
                )}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-3">
                    Description
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {selectedTicket.description}
                    </p>
                  </div>
                </div>

                {/* Comments Section */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-3">
                    Comments ({selectedTicket.comments.length})
                  </h4>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {selectedTicket.comments.map((comment) => (
                      <div
                        key={comment.id}
                        className="bg-gray-50 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <div
                              className={`h-8 w-8 rounded-full flex items-center justify-center ${
                                comment.authorType === "support"
                                  ? "bg-blue-100"
                                  : "bg-green-100"
                              }`}
                            >
                              {comment.authorType === "support" ? (
                                <Shield className="h-4 w-4 text-blue-600" />
                              ) : (
                                <User className="h-4 w-4 text-green-600" />
                              )}
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900">
                                {comment.author}
                              </p>
                              <p className="text-xs text-gray-500">
                                {new Date(comment.createdAt).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          {comment.isInternal && (
                            <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold px-2 py-1 rounded">
                              Internal
                            </span>
                          )}
                        </div>
                        <p className="text-gray-700 text-sm whitespace-pre-wrap">
                          {comment.message}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Add Comment */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Add Comment
                    </label>
                    <div className="space-y-3">
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Type your comment here..."
                        rows={3}
                        className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="internal-comment"
                            className="mr-2"
                            checked={isInternalComment}
                            onChange={(e) =>
                              setIsInternalComment(e.target.checked)
                            }
                          />
                          <label
                            htmlFor="internal-comment"
                            className="text-sm text-gray-600"
                          >
                            Internal comment (not visible to customer)
                          </label>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() =>
                              handleAddComment(
                                selectedTicket.id,
                                newComment,
                                isInternalComment
                              )
                            }
                            disabled={!newComment.trim()}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                          >
                            <Send className="h-4 w-4 mr-2" />
                            Add Comment
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ticket Sidebar */}
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Ticket Information
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-gray-600">Status:</span>
                      <select
                        value={selectedTicket.status}
                        onChange={(e) =>
                          handleTicketStatusUpdate(
                            selectedTicket.id,
                            e.target.value as SupportTicket["status"]
                          )
                        }
                        className="ml-2 border border-gray-300 rounded px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="open">Open</option>
                        <option value="in-progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                    <div>
                      <span className="text-gray-600">Priority:</span>
                      <span
                        className={`ml-2 px-2 py-1 text-xs font-semibold rounded ${getPriorityColor(
                          selectedTicket.priority
                        )}`}
                      >
                        {selectedTicket.priority}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Category:</span>
                      <span className="ml-2 text-gray-900 capitalize">
                        {selectedTicket.category.replace("-", " ")}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Assigned to:</span>
                      <select
                        value={selectedTicket.assignedTo || ""}
                        onChange={(e) => {
                          const assignedTo = e.target.value;
                          const assignedToName =
                            assignedTo === "support_001"
                              ? "Sarah Wilson"
                              : assignedTo === "support_002"
                              ? "David Chen"
                              : assignedTo === "support_003"
                              ? "Mark Thompson"
                              : "";
                          handleAssignTicket(
                            selectedTicket.id,
                            assignedTo,
                            assignedToName
                          );
                        }}
                        className="ml-2 border border-gray-300 rounded px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Unassigned</option>
                        <option value="support_001">Sarah Wilson</option>
                        <option value="support_002">David Chen</option>
                        <option value="support_003">Mark Thompson</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Customer Information
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-600">Organization:</span>
                      <p className="text-gray-900 font-medium">
                        {selectedTicket.organizationName}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600">Contact:</span>
                      <p className="text-gray-900">{selectedTicket.userName}</p>
                      <p className="text-gray-600">
                        {selectedTicket.userEmail}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Timeline
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-600">Created:</span>
                      <p className="text-gray-900">
                        {new Date(selectedTicket.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600">Updated:</span>
                      <p className="text-gray-900">
                        {new Date(selectedTicket.updatedAt).toLocaleString()}
                      </p>
                    </div>
                    {selectedTicket.resolvedAt && (
                      <div>
                        <span className="text-gray-600">Resolved:</span>
                        <p className="text-gray-900">
                          {new Date(selectedTicket.resolvedAt).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {selectedTicket.tags.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">
                      Tags
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedTicket.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded"
                        >
                          <Tag className="h-3 w-3 inline mr-1" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedTicket.attachments.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">
                      Attachments
                    </h4>
                    <div className="space-y-2">
                      {selectedTicket.attachments.map((attachment, index) => (
                        <div key={index} className="flex items-center text-sm">
                          <Paperclip className="h-4 w-4 text-gray-400 mr-2" />
                          <button
                            type="button"
                            onClick={() => window.open(attachment, "_blank")}
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            {attachment}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Ticket Modal */}
      {showCreateTicket && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Create Support Ticket
              </h3>
              <button
                onClick={() => setShowCreateTicket(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                const newTicket: SupportTicket = {
                  id: `ticket_${Date.now()}`,
                  ticketNumber: `TICK-2025-${String(
                    tickets.length + 1
                  ).padStart(3, "0")}`,
                  subject: formData.get("subject") as string,
                  description: formData.get("description") as string,
                  priority: formData.get(
                    "priority"
                  ) as SupportTicket["priority"],
                  status: "open",
                  category: formData.get(
                    "category"
                  ) as SupportTicket["category"],
                  organizationId: formData.get("organizationId") as string,
                  organizationName:
                    organizations.find(
                      (org) => org.id === formData.get("organizationId")
                    )?.name || "Unknown Organization",
                  userEmail: formData.get("userEmail") as string,
                  userName: formData.get("userName") as string,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  tags: (formData.get("tags") as string)
                    .split(",")
                    .map((tag) => tag.trim())
                    .filter((tag) => tag),
                  attachments: [],
                  comments: [],
                };
                setTickets([newTicket, ...tickets]);
                setShowCreateTicket(false);
              }}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    name="subject"
                    type="text"
                    required
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Brief description of the issue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization *
                  </label>
                  <select
                    name="organizationId"
                    required
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Organization</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  name="description"
                  required
                  rows={4}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Detailed description of the issue or request"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority *
                  </label>
                  <select
                    name="priority"
                    required
                    defaultValue="medium"
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    name="category"
                    required
                    defaultValue="general"
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="technical">Technical</option>
                    <option value="billing">Billing</option>
                    <option value="account">Account</option>
                    <option value="feature-request">Feature Request</option>
                    <option value="bug">Bug Report</option>
                    <option value="general">General</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    User Name *
                  </label>
                  <input
                    name="userName"
                    type="text"
                    required
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Customer's name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    User Email *
                  </label>
                  <input
                    name="userEmail"
                    type="email"
                    required
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="customer@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags
                </label>
                <input
                  name="tags"
                  type="text"
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter tags separated by commas (e.g., urgent, login, bug)"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Separate multiple tags with commas
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowCreateTicket(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 flex items-center"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Ticket
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Super Admin Dashboard
            </h1>
            <p className="text-gray-600">
              Manage organizations, plans, and system analytics
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center space-x-4">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log("🖱️ Logout button clicked - event triggered");
                handleLogout();
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 transition-colors"
              type="button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab("overview")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "overview"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <BarChart3 className="h-4 w-4 inline mr-2" />
              Overview
            </button>
            <button
              onClick={() => setActiveTab("organizations")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "organizations"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <Building2 className="h-4 w-4 inline mr-2" />
              Organizations
            </button>
            <button
              onClick={() => setActiveTab("plans")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "plans"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <CreditCard className="h-4 w-4 inline mr-2" />
              Plans
            </button>
            <button
              onClick={() => setActiveTab("progressive-plans")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "progressive-plans"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <Layers className="h-4 w-4 inline mr-2" />
              Progressive Plans
            </button>
            <button
              onClick={() => setActiveTab("analytics")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "analytics"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <TrendingUp className="h-4 w-4 inline mr-2" />
              Analytics
            </button>
            <button
              onClick={() => setActiveTab("tickets")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "tickets"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <Ticket className="h-4 w-4 inline mr-2" />
              Support Tickets
            </button>
            <button
              onClick={() => setActiveTab("settings")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "settings"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <Settings className="h-4 w-4 inline mr-2" />
              Settings
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && renderOverview()}
        {activeTab === "organizations" && renderOrganizations()}
        {activeTab === "plans" && renderPlans()}
        {activeTab === "progressive-plans" && renderProgressivePlans()}
        {activeTab === "analytics" && renderAnalytics()}
        {activeTab === "tickets" && renderSupportTickets()}
        {activeTab === "settings" && renderSettings()}

        {/* Organization Detail Modal */}
        {selectedOrganization && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Organization Details
                </h3>
                <button
                  onClick={() => setSelectedOrganization(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Name
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedOrganization.name}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Industry
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedOrganization.industry}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Size
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedOrganization.size}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Plan
                    </label>
                    <p className="text-sm text-gray-900">
                      {getPlanBadge(selectedOrganization.plan)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Status
                    </label>
                    <p className="text-sm text-gray-900">
                      {getStatusBadge(selectedOrganization.status)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Monthly Revenue
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedOrganization.monthlyRevenue > 0
                        ? formatCurrency(selectedOrganization.monthlyRevenue)
                        : "-"}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-gray-500">
                      Progressive Plan Balances
                    </label>
                    <div className="mt-1 space-y-2">
                      {selectedOrganization.progressivePlanBalances.length >
                      0 ? (
                        selectedOrganization.progressivePlanBalances.map(
                          (balance, index) => (
                            <div
                              key={index}
                              className={`p-4 rounded-lg border-l-4 ${
                                balance.progressivePricingType === "STORIES"
                                  ? "bg-blue-50 border-blue-400"
                                  : balance.progressivePricingType === "USERS"
                                  ? "bg-green-50 border-green-400"
                                  : "bg-gray-50 border-gray-400"
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div className="flex items-center space-x-2">
                                  <div
                                    className={`p-2 rounded-full ${
                                      balance.progressivePricingType ===
                                      "STORIES"
                                        ? "bg-blue-100"
                                        : balance.progressivePricingType ===
                                          "USERS"
                                        ? "bg-green-100"
                                        : "bg-gray-100"
                                    }`}
                                  >
                                    {balance.progressivePricingType ===
                                    "STORIES" ? (
                                      <Layers
                                        className={`h-4 w-4 text-blue-600`}
                                      />
                                    ) : balance.progressivePricingType ===
                                      "USERS" ? (
                                      <Users
                                        className={`h-4 w-4 text-green-600`}
                                      />
                                    ) : (
                                      <Tag
                                        className={`h-4 w-4 text-gray-600`}
                                      />
                                    )}
                                  </div>
                                  <div>
                                    <div
                                      className={`text-sm font-medium ${
                                        balance.progressivePricingType ===
                                        "STORIES"
                                          ? "text-blue-900"
                                          : balance.progressivePricingType ===
                                            "USERS"
                                          ? "text-green-900"
                                          : "text-gray-900"
                                      }`}
                                    >
                                      {balance.progressivePricingType} Credits
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      Plan: {balance.planKey}{" "}
                                      {balance.isDefault && "(Default)"}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm font-medium text-gray-900">
                                    {balance.balance} / {balance.quota}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {(
                                      (balance.balance / balance.quota) *
                                      100
                                    ).toFixed(1)}
                                    % remaining
                                  </div>
                                </div>
                              </div>
                              <div className="mt-3">
                                <div className="w-full bg-gray-200 rounded-full h-3">
                                  <div
                                    className={`h-3 rounded-full transition-all duration-300 ${
                                      balance.progressivePricingType ===
                                      "STORIES"
                                        ? balance.balance / balance.quota > 0.5
                                          ? "bg-blue-500"
                                          : balance.balance / balance.quota >
                                            0.2
                                          ? "bg-blue-400"
                                          : "bg-red-500"
                                        : balance.progressivePricingType ===
                                          "USERS"
                                        ? balance.balance / balance.quota > 0.5
                                          ? "bg-green-500"
                                          : balance.balance / balance.quota >
                                            0.2
                                          ? "bg-yellow-500"
                                          : "bg-red-500"
                                        : balance.balance / balance.quota > 0.5
                                        ? "bg-gray-500"
                                        : balance.balance / balance.quota > 0.2
                                        ? "bg-yellow-500"
                                        : "bg-red-500"
                                    }`}
                                    style={{
                                      width: `${
                                        (balance.balance / balance.quota) * 100
                                      }%`,
                                    }}
                                  ></div>
                                </div>
                                <div className="flex justify-between text-xs text-gray-500 mt-1">
                                  <span>0</span>
                                  <span>{balance.quota}</span>
                                </div>
                              </div>
                            </div>
                          )
                        )
                      ) : (
                        <div className="text-sm text-gray-500">
                          No progressive plan balances
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Users
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedOrganization.users}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Teams
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedOrganization.teams}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Admin Email
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedOrganization.adminEmail}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Website
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedOrganization.website || "-"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Created At
                    </label>
                    <p className="text-sm text-gray-900">
                      {formatDate(selectedOrganization.createdAt)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Last Active
                    </label>
                    <p className="text-sm text-gray-900">
                      {formatDate(selectedOrganization.lastActive)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Organization Edit Modal */}
        {editingOrganization && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Edit Organization
                </h3>
                <button
                  onClick={() => setEditingOrganization(null)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={organizationUpdateLoading}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  const updatedData = {
                    name: formData.get("name") as string,
                    industry: formData.get("industry") as string,
                    size: formData.get("size") as string,
                    website: formData.get("website") as string,
                    adminEmail: formData.get("adminEmail") as string,
                    status: formData.get("status") as
                      | "active"
                      | "suspended"
                      | "trial",
                  };
                  await updateOrganization(editingOrganization.id, updatedData);
                }}
                className="space-y-4"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Organization Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      defaultValue={editingOrganization.name}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Industry
                    </label>
                    <input
                      type="text"
                      name="industry"
                      defaultValue={editingOrganization.industry}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Organization Size
                    </label>
                    <select
                      name="size"
                      defaultValue={editingOrganization.size}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="startup">Startup (1-10)</option>
                      <option value="small">Small (11-50)</option>
                      <option value="medium">Medium (51-200)</option>
                      <option value="large">Large (201-1000)</option>
                      <option value="enterprise">Enterprise (1000+)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Status
                    </label>
                    <select
                      name="status"
                      defaultValue={editingOrganization.status}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                      <option value="trial">Trial</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Website
                    </label>
                    <input
                      type="url"
                      name="website"
                      defaultValue={editingOrganization.website || ""}
                      placeholder="https://example.com"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                      <div className="flex">
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-blue-800">
                            Progressive Plan Balances
                          </h3>
                          <div className="mt-1 text-sm text-blue-700">
                            Progressive plan balances are automatically managed
                            by the system based on plan assignments and usage.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Admin Email
                    </label>
                    <input
                      type="email"
                      name="adminEmail"
                      defaultValue={editingOrganization.adminEmail}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setEditingOrganization(null)}
                    disabled={organizationUpdateLoading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={organizationUpdateLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 flex items-center"
                  >
                    {organizationUpdateLoading && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    )}
                    {organizationUpdateLoading
                      ? "Updating..."
                      : "Update Organization"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Create Organization Modal */}
        {showCreateOrg && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Create New Organization
                </h3>
                <button
                  onClick={() => setShowCreateOrg(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={organizationCreateLoading}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  const organizationData = {
                    name: formData.get("name") as string,
                    industry: formData.get("industry") as string,
                    size: formData.get("size") as string,
                    website: formData.get("website") as string,
                    adminEmail: formData.get("adminEmail") as string,
                    adminPassword: formData.get("adminPassword") as string,
                    status: formData.get("status") as
                      | "active"
                      | "suspended"
                      | "trial",
                  };

                  const result = await createOrganization(organizationData);
                  if (!result.success) {
                    alert(`Failed to create organization: ${result.error}`);
                  }
                }}
                className="space-y-4"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Organization Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="e.g., Acme Corporation"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Industry *
                    </label>
                    <input
                      type="text"
                      name="industry"
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="e.g., Technology, Healthcare"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Organization Size *
                    </label>
                    <select
                      name="size"
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">Select size...</option>
                      <option value="startup">Startup (1-10)</option>
                      <option value="small">Small (11-50)</option>
                      <option value="medium">Medium (51-200)</option>
                      <option value="large">Large (201-1000)</option>
                      <option value="enterprise">Enterprise (1000+)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Initial Status
                    </label>
                    <select
                      name="status"
                      defaultValue="active"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="active">Active</option>
                      <option value="trial">Trial</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Website
                    </label>
                    <input
                      type="url"
                      name="website"
                      placeholder="https://example.com"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                      <div className="flex">
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-blue-800">
                            Progressive Plan Information
                          </h3>
                          <div className="mt-1 text-sm text-blue-700">
                            Progressive plan balances will be automatically set
                            up based on the organization's plan assignment after
                            creation.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="text-md font-medium text-gray-900 mb-3">
                    Admin Account Details
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Admin Email *
                      </label>
                      <input
                        type="email"
                        name="adminEmail"
                        required
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="admin@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Admin Password *
                      </label>
                      <input
                        type="password"
                        name="adminPassword"
                        required
                        minLength={8}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Min 8 characters"
                      />
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    An admin account will be automatically created with these
                    credentials. The admin will receive an email with login
                    instructions.
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowCreateOrg(false)}
                    disabled={organizationCreateLoading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={organizationCreateLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 flex items-center"
                  >
                    {organizationCreateLoading && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    )}
                    {organizationCreateLoading
                      ? "Creating..."
                      : "Create Organization"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Progressive Plan Topup Modal */}
        {showTopupModal && topupOrganization && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Topup Progressive Plan - {topupOrganization.name}
                </h3>
                <button
                  onClick={() => {
                    setShowTopupModal(false);
                    setTopupOrganization(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={topupLoading}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Current Balances Display */}
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-900 mb-3">
                  Current Progressive Plan Balances
                </h4>
                <div className="space-y-2">
                  {topupOrganization.progressivePlanBalances.length > 0 ? (
                    topupOrganization.progressivePlanBalances.map(
                      (balance, index) => (
                        <div
                          key={index}
                          className={`p-3 rounded-lg border-l-4 ${
                            balance.progressivePricingType === "STORIES"
                              ? "bg-blue-50 border-blue-400"
                              : balance.progressivePricingType === "USERS"
                              ? "bg-green-50 border-green-400"
                              : "bg-gray-50 border-gray-400"
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <div
                                className={`text-sm font-medium ${
                                  balance.progressivePricingType === "STORIES"
                                    ? "text-blue-900"
                                    : balance.progressivePricingType === "USERS"
                                    ? "text-green-900"
                                    : "text-gray-900"
                                }`}
                              >
                                {balance.progressivePricingType} Credits
                              </div>
                              <div className="text-xs text-gray-500">
                                Plan ID: {balance.planId} • {balance.planKey}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-medium text-gray-900">
                                {balance.balance} / {balance.quota}
                              </div>
                              <div className="text-xs text-gray-500">
                                {(
                                  (balance.balance / balance.quota) *
                                  100
                                ).toFixed(1)}
                                % remaining
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    )
                  ) : (
                    <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">
                      No progressive plan balances found
                    </div>
                  )}
                </div>
              </div>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  const topupData = {
                    organizationId: parseInt(topupOrganization.id),
                    planId: parseInt(formData.get("planId") as string),
                    amount: parseInt(formData.get("amount") as string),
                    adminNote: formData.get("adminNote") as string,
                    adminEmail: formData.get("adminEmail") as string,
                  };

                  const result = await topupProgressivePlan(topupData);
                  if (!result.success) {
                    alert(`Failed to topup progressive plan: ${result.error}`);
                  }
                }}
                className="space-y-4"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Plan ID *
                    </label>
                    <select
                      name="planId"
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">Select a plan to topup...</option>
                      {topupOrganization.progressivePlanBalances.map(
                        (balance) => (
                          <option key={balance.planId} value={balance.planId}>
                            {balance.progressivePricingType} - {balance.planKey}{" "}
                            (ID: {balance.planId})
                          </option>
                        )
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Topup Amount *
                    </label>
                    <input
                      type="number"
                      name="amount"
                      min="1"
                      required
                      placeholder="Enter amount to add"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Admin Email *
                    </label>
                    <input
                      type="email"
                      name="adminEmail"
                      required
                      placeholder="Your admin email"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Admin Note *
                    </label>
                    <textarea
                      name="adminNote"
                      rows={3}
                      required
                      placeholder="Reason for topup (e.g., 'Granting seats for partner')"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowTopupModal(false);
                      setTopupOrganization(null);
                    }}
                    disabled={topupLoading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      topupLoading ||
                      topupOrganization.progressivePlanBalances.length === 0
                    }
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 flex items-center"
                  >
                    {topupLoading && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    )}
                    <Plus className="h-4 w-4 mr-2" />
                    {topupLoading
                      ? "Processing Topup..."
                      : "Topup Progressive Plan"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Plan Details Modal */}
        {selectedPlan && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <CreditCard className="h-6 w-6 text-blue-600" />
                  <h3 className="text-xl font-semibold text-gray-900">
                    {selectedPlan.name} Plan Details
                  </h3>
                  {selectedPlan.isMostPopular && (
                    <span className="bg-purple-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                      Most Popular
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setSelectedPlan(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Basic Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Plan ID
                      </label>
                      <p className="text-sm text-gray-900">{selectedPlan.id}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Plan Key
                      </label>
                      <p className="text-sm text-gray-900">
                        {selectedPlan.key}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Name
                      </label>
                      <p className="text-sm text-gray-900">
                        {selectedPlan.name}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Description
                      </label>
                      <p className="text-sm text-gray-900">
                        {selectedPlan.description}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Organizations Using
                      </label>
                      <p className="text-sm text-gray-900">
                        {selectedPlan.organizationCount}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Created
                      </label>
                      <p className="text-sm text-gray-900">
                        {formatDate(selectedPlan.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Billing Options */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Billing Options
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedPlan.billingOptions.map((billing, index) => (
                      <div
                        key={billing.id}
                        className={`border rounded-lg p-4 ${
                          billing.isDefault
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900">
                            {billing.billingCycle}
                          </span>
                          {billing.isDefault && (
                            <span className="bg-blue-500 text-white px-2 py-1 rounded text-xs">
                              Default
                            </span>
                          )}
                        </div>
                        <div className="space-y-1">
                          <p className="text-lg font-bold text-gray-900">
                            {billing.formattedPrice}
                          </p>
                          <p className="text-xs text-gray-500">
                            ({billing.priceCents} cents)
                          </p>
                          {billing.savingsPercentage && (
                            <p className="text-xs text-green-600 font-medium">
                              Save {billing.savingsPercentage}%
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Features */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Features
                  </h4>
                  <div className="bg-white border rounded-lg divide-y divide-gray-200">
                    {selectedPlan.features.map((feature, index) => (
                      <div
                        key={feature.featureId}
                        className="p-4 flex items-start space-x-3"
                      >
                        <div className="flex-shrink-0 mt-1">
                          <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {feature.featureName}
                          </p>
                          <p className="text-sm text-gray-600">
                            {feature.value}
                          </p>
                          <p className="text-xs text-gray-400">
                            Code: {feature.featureCode}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Plan Limits */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Plan Limits
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Maximum Users
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedPlan.limits.maxUsers === 999999
                            ? "Unlimited"
                            : selectedPlan.limits.maxUsers.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Maximum Teams
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedPlan.limits.maxTeams === 999999
                            ? "Unlimited"
                            : selectedPlan.limits.maxTeams.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Storage
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedPlan.limits.formattedStorage}
                        </p>
                        {selectedPlan.limits.storageBytes && (
                          <p className="text-xs text-gray-400">
                            ({selectedPlan.limits.storageBytes.toLocaleString()}{" "}
                            bytes)
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Support Type
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedPlan.limits.supportType}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timestamps */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Timestamps
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Created At
                        </label>
                        <p className="text-sm text-gray-900">
                          {formatDate(selectedPlan.createdAt)}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Last Updated
                        </label>
                        <p className="text-sm text-gray-900">
                          {formatDate(selectedPlan.updatedAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setSelectedPlan(null)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setEditingPlan(selectedPlan);
                      setSelectedPlan(null);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
                  >
                    Edit Plan
                  </button>
                  <button className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700">
                    Duplicate Plan
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Plan Modal */}
        {editingPlan && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <Edit className="h-6 w-6 text-blue-600" />
                  <h3 className="text-xl font-semibold text-gray-900">
                    Edit {editingPlan.name} Plan
                  </h3>
                  {editingPlan.isMostPopular && (
                    <span className="bg-purple-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                      Most Popular
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setEditingPlan(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    const formData = new FormData(e.currentTarget);
                    const result = await handlePlanEdit(formData);
                    if (result && result.success) {
                      // Show success message
                      alert("Plan updated successfully!");
                    } else {
                      // Show error message
                      alert(
                        "Failed to update plan: " +
                          (result?.error || "Unknown error")
                      );
                    }
                  } catch (error) {
                    console.error("Error updating plan:", error);
                    alert("Failed to update plan: Network error");
                  }
                }}
                className="space-y-6"
              >
                {/* Basic Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Basic Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Plan Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        defaultValue={editingPlan.name}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <input
                        type="text"
                        name="description"
                        defaultValue={editingPlan.description}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="isMostPopular"
                          value="true"
                          defaultChecked={editingPlan.isMostPopular}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">
                          Mark as Most Popular
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Billing Options */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Billing Options
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {editingPlan.billingOptions.map((billing, index) => (
                      <div
                        key={billing.id}
                        className={`border rounded-lg p-4 ${
                          billing.isDefault
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900">
                            {billing.billingCycle}
                          </span>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              name={`billing_${index}_isDefault`}
                              value="true"
                              defaultChecked={billing.isDefault}
                              className="mr-1"
                            />
                            <span className="text-xs">Default</span>
                          </label>
                        </div>
                        <div className="space-y-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700">
                              Price (cents)
                            </label>
                            <input
                              type="number"
                              name={`billing_${index}_priceCents`}
                              defaultValue={billing.priceCents}
                              required
                              min="0"
                              className="block w-full border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div className="text-xs text-gray-500">
                            Current: {billing.formattedPrice}
                            {billing.savingsPercentage && (
                              <span className="text-green-600 ml-1">
                                (Save {billing.savingsPercentage}%)
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Features */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Features
                  </h4>
                  <div className="space-y-3">
                    {editingPlan.features.map((feature, index) => (
                      <div
                        key={feature.featureId}
                        className="border rounded-lg p-4"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700">
                              Feature Name
                            </label>
                            <input
                              type="text"
                              name={`feature_${index}_name`}
                              defaultValue={feature.featureName}
                              required
                              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">
                              Feature Value
                            </label>
                            <input
                              type="text"
                              name={`feature_${index}_value`}
                              defaultValue={feature.value}
                              required
                              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <p className="text-xs text-gray-500">
                              Code: {feature.featureCode}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Plan Limits */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Plan Limits
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Maximum Users
                        </label>
                        <input
                          type="number"
                          name="maxUsers"
                          defaultValue={
                            editingPlan.limits.maxUsers === 999999
                              ? ""
                              : editingPlan.limits.maxUsers
                          }
                          placeholder="Leave empty for unlimited"
                          min="1"
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Current:{" "}
                          {editingPlan.limits.maxUsers === 999999
                            ? "Unlimited"
                            : editingPlan.limits.maxUsers.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Maximum Teams
                        </label>
                        <input
                          type="number"
                          name="maxTeams"
                          defaultValue={
                            editingPlan.limits.maxTeams === 999999
                              ? ""
                              : editingPlan.limits.maxTeams
                          }
                          placeholder="Leave empty for unlimited"
                          min="1"
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Current:{" "}
                          {editingPlan.limits.maxTeams === 999999
                            ? "Unlimited"
                            : editingPlan.limits.maxTeams.toLocaleString()}
                        </p>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Support Type
                        </label>
                        <input
                          type="text"
                          name="supportType"
                          defaultValue={editingPlan.limits.supportType}
                          required
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-xs text-gray-500">
                          Storage: {editingPlan.limits.formattedStorage}{" "}
                          (Storage limits are managed separately)
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setEditingPlan(null)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateLoading}
                    className={`px-4 py-2 rounded-md text-sm font-medium text-white ${
                      updateLoading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {updateLoading ? "Updating..." : "Update Plan"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
